import type { UIMessage } from "ai";
import { useEffect, useRef } from "react";

import {
  incrementImpressionCount,
  readCooldownLastTs,
  readImpressionCount,
  readManualCloseTs,
  readRandomUsedKeys,
  readSessionDone,
  readStoredConfigId,
  readVisitorEngagedHint,
  setSessionDone,
  writeCooldownLastTs,
  writeRandomUsedKeys,
  writeStoredConfigId,
} from "./auto-launch-storage";
import type { WidgetConfig } from "./widget-config";
import {
  normalizeGreetMessage,
  parseAutoLaunchPublicConfig,
  type AutoLaunchPublicConfig,
} from "@/lib/widget/autolaunch-public-config";
import {
  decideAutoLaunch,
  detectDeviceFromUserAgent,
  mapLegacyFrequency,
} from "@/lib/widget/autolaunch-rules";
import { trackAutolaunchEvent } from "@/components/widget/autolaunch-telemetry";

const MANUAL_CLOSE_MINUTES = 5;
const MANUAL_CLOSE_COOLDOWN_MS = MANUAL_CLOSE_MINUTES * 60 * 1000;
const BASE_GREET_MS = 400;
const SEQUENTIAL_GAP_MS = 1500;

function newUiMessageId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `s2r-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function appendAssistantGreeting(
  setMessages: (updater: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => void,
  text: string,
): void {
  const trimmed = text.trim();
  if (!trimmed) {
    return;
  }
  const id = newUiMessageId();
  setMessages((prev) => [
    ...prev,
    {
      id,
      role: "assistant",
      parts: [{ type: "text", text: trimmed, state: "done" }],
    },
  ]);
}

function msgKey(msg: { id?: string; text: string }): string {
  try {
    return String(msg.id || msg.text).slice(0, 128);
  } catch {
    return "";
  }
}

export interface UseAutoLaunchParams {
  readonly config: WidgetConfig;
  readonly isStateless: boolean;
  readonly panelOpen: boolean;
  readonly hasActiveConversation: boolean;
  readonly hasResumedHistory: boolean;
  /** Persistent visitor session id; flows into autolaunch telemetry for funnel attribution. */
  readonly sessionId: string;
  readonly setMessages: (messages: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => void;
  readonly setIsOpen: (open: boolean) => void;
  /**
   * Fires immediately after the panel auto-launches (i.e. right after the
   * `opened` telemetry event is queued). Receives the resolved
   * `config_id` so the caller can thread it into the follow-up tracker
   * for `dismissed` / `user_replied` attribution (#155 Item 3).
   *
   * Distinct from `setIsOpen(true)` because the latter also fires for
   * FAB-initiated opens; `onFired` fires *only* for auto-launches.
   */
  readonly onFired?: (configId: string | null) => void;
}

function autolaunchUrl(config: WidgetConfig): string {
  const base = config.apiOrigin.replace(/\/$/, "");
  const q = new URLSearchParams({
    tenant_id: config.tenantId,
    site_id: config.siteId,
  });
  return `${base}/api/engage/autolaunch?${q.toString()}`;
}

/**
 * Ports legacy `autoLaunchController.js` scheduling, storage gates, and greeting delivery
 * against Vercel AI SDK `setMessages` (assistant greetings are local UI only — no API round-trip).
 */
export function useAutoLaunch({
  config,
  isStateless,
  panelOpen,
  hasActiveConversation,
  hasResumedHistory,
  sessionId,
  setMessages,
  setIsOpen,
  onFired,
}: UseAutoLaunchParams): void {
  const hasActiveRef = useRef(hasActiveConversation);
  const hasResumedRef = useRef(hasResumedHistory);
  const statelessRef = useRef(isStateless);
  const panelOpenRef = useRef(panelOpen);
  const sessionIdRef = useRef(sessionId);
  const setMessagesRef = useRef(setMessages);
  const setIsOpenRef = useRef(setIsOpen);
  const onFiredRef = useRef(onFired);

  useEffect(() => {
    hasActiveRef.current = hasActiveConversation;
  }, [hasActiveConversation]);
  useEffect(() => {
    hasResumedRef.current = hasResumedHistory;
  }, [hasResumedHistory]);
  useEffect(() => {
    statelessRef.current = isStateless;
  }, [isStateless]);
  useEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  useEffect(() => {
    setMessagesRef.current = setMessages;
  }, [setMessages]);
  useEffect(() => {
    setIsOpenRef.current = setIsOpen;
  }, [setIsOpen]);
  useEffect(() => {
    onFiredRef.current = onFired;
  }, [onFired]);

  const launchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const greetTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    let cancelled = false;

    function clearLaunchTimer(): void {
      if (launchTimerRef.current != null) {
        clearTimeout(launchTimerRef.current);
        launchTimerRef.current = null;
      }
    }

    function clearGreetTimers(): void {
      greetTimersRef.current.forEach((t) => clearTimeout(t));
      greetTimersRef.current = [];
    }

    function scheduleGreets(conf: AutoLaunchPublicConfig): void {
      clearGreetTimers();
      const msgsAll = conf.greet_messages.map(normalizeGreetMessage).filter((m) => m.text.length > 0);
      const order = String(conf.message_order || "sequential");
      const tenantId = config.tenantId;
      const siteId = config.siteId;

      if (order === "random" && msgsAll.length > 0) {
        let used = readRandomUsedKeys(tenantId, siteId);
        let remaining = msgsAll.filter((m) => {
          const k = msgKey(m);
          return k && !used.includes(k);
        });
        if (remaining.length === 0) {
          used = [];
          remaining = msgsAll.slice();
          writeRandomUsedKeys(tenantId, siteId, []);
        }
        const idx = Math.floor(Math.random() * remaining.length);
        const one = remaining[idx];
        if (!one) {
          return;
        }
        const k1 = msgKey(one);
        const d1 = Number(one.delay_ms ?? 0);
        const greetTimer = setTimeout(() => {
          if (cancelled) {
            return;
          }
          appendAssistantGreeting(setMessagesRef.current, one.text);
          trackAutolaunchEvent(config, {
            type: "assistant_greet_sent",
            config_id: conf.config_id ?? null,
            message_id: k1 || null,
            session_id: sessionIdRef.current,
          });
        }, Math.max(0, d1) + BASE_GREET_MS);
        greetTimersRef.current.push(greetTimer);
        if (k1) {
          used.push(k1);
          writeRandomUsedKeys(tenantId, siteId, used);
        }
        return;
      }

      let accDelay = BASE_GREET_MS;
      msgsAll.forEach((m, i) => {
        const hasExplicit = m.delay_ms !== undefined && m.delay_ms !== null;
        const step = hasExplicit ? Number(m.delay_ms) : i === 0 ? 0 : SEQUENTIAL_GAP_MS;
        const safeStep = Number.isFinite(step) && step >= 0 ? step : 0;
        accDelay += safeStep;
        const text = m.text;
        const fireAt = accDelay;
        const greetTimer = setTimeout(() => {
          if (cancelled) {
            return;
          }
          appendAssistantGreeting(setMessagesRef.current, text);
          trackAutolaunchEvent(config, {
            type: "assistant_greet_sent",
            config_id: conf.config_id ?? null,
            message_id: msgKey(m) || null,
            session_id: sessionIdRef.current,
          });
        }, fireAt);
        greetTimersRef.current.push(greetTimer);
      });
    }

    function currentPath(): string {
      if (typeof window === "undefined") return "/";
      return window.location.pathname || "/";
    }

    function currentLanguage(): string {
      if (typeof navigator === "undefined") return "";
      return navigator.language || "";
    }

    function currentDevice(): "desktop" | "tablet" | "mobile" {
      if (typeof navigator === "undefined") return "desktop";
      return detectDeviceFromUserAgent(navigator.userAgent);
    }

    function scheduleAutoLaunch(conf: AutoLaunchPublicConfig): void {
      clearLaunchTimer();

      const nowMs = Date.now();
      const freq = mapLegacyFrequency(conf.frequency);

      // #155 Item 4 — operator-opt-in suppression for returning visitors.
      // Layered with the prop-driven `hasResumedHistory` so callers can
      // still inject an external signal (e.g. a future server existence
      // check) without losing the storage-hint behavior.
      const effectiveHasResumedHistory =
        hasResumedRef.current ||
        (conf.suppress_for_returning_visitors === true &&
          readVisitorEngagedHint(config.tenantId, config.siteId));

      const decision = decideAutoLaunch({
        enabled: conf.enabled,
        isStateless: statelessRef.current,
        hasActiveConversation: hasActiveRef.current,
        hasResumedHistory: effectiveHasResumedHistory,
        sessionDone: readSessionDone(config.tenantId, config.siteId),
        frequency: freq,
        cooldownSec: conf.cooldown_sec ?? null,
        lastFireMs: readCooldownLastTs(config.tenantId, config.siteId),
        manualCloseLastMs: readManualCloseTs(config.tenantId, config.siteId),
        manualCloseCooldownMs: MANUAL_CLOSE_COOLDOWN_MS,
        maxImpressionsPerSession: conf.max_impressions_per_session ?? 1,
        impressionCount: readImpressionCount(config.tenantId, config.siteId),
        nowMs,
        path: currentPath(),
        pathInclude: conf.path_include,
        pathExclude: conf.path_exclude,
        device: currentDevice(),
        deviceInclude: conf.device_include,
        language: currentLanguage(),
        localeInclude: conf.locale_include,
        startAt: conf.start_at,
        endAt: conf.end_at,
      });

      if (!decision.fire) {
        return;
      }

      const delayMs = Math.max(0, Number(conf.launch_delay_sec ?? 0) * 1000);
      launchTimerRef.current = setTimeout(() => {
        launchTimerRef.current = null;
        if (cancelled) {
          return;
        }
        // Re-evaluate gates at fire time. Storage state (impressions, manual
        // close, cooldown) and runtime state (active convo, panel open,
        // schedule window) can flip during the launch_delay window — we must
        // not fire stale.
        // Re-read the engaged hint at fire time too — the visitor may
        // have sent their first message during the launch_delay window
        // in another tab, in which case we must respect the freshly
        // written hint and abort the auto-launch.
        const reEvalHasResumedHistory =
          hasResumedRef.current ||
          (conf.suppress_for_returning_visitors === true &&
            readVisitorEngagedHint(config.tenantId, config.siteId));
        const reEval = decideAutoLaunch({
          enabled: conf.enabled,
          isStateless: statelessRef.current,
          hasActiveConversation: hasActiveRef.current,
          hasResumedHistory: reEvalHasResumedHistory,
          sessionDone: readSessionDone(config.tenantId, config.siteId),
          frequency: freq,
          cooldownSec: conf.cooldown_sec ?? null,
          lastFireMs: readCooldownLastTs(config.tenantId, config.siteId),
          manualCloseLastMs: readManualCloseTs(config.tenantId, config.siteId),
          manualCloseCooldownMs: MANUAL_CLOSE_COOLDOWN_MS,
          maxImpressionsPerSession: conf.max_impressions_per_session ?? 1,
          impressionCount: readImpressionCount(config.tenantId, config.siteId),
          nowMs: Date.now(),
          path: currentPath(),
          pathInclude: conf.path_include,
          pathExclude: conf.path_exclude,
          device: currentDevice(),
          deviceInclude: conf.device_include,
          language: currentLanguage(),
          localeInclude: conf.locale_include,
          startAt: conf.start_at,
          endAt: conf.end_at,
        });
        if (!reEval.fire) {
          return;
        }
        if (panelOpenRef.current) {
          return;
        }

        setIsOpenRef.current(true);
        trackAutolaunchEvent(config, {
          type: "impression",
          config_id: conf.config_id ?? null,
          session_id: sessionIdRef.current,
        });
        trackAutolaunchEvent(config, {
          type: "opened",
          config_id: conf.config_id ?? null,
          session_id: sessionIdRef.current,
        });
        // #155 Item 3 — notify the caller so it can arm the follow-up
        // tracker (dismissed / user_replied). Must run AFTER the
        // `opened` telemetry so the order on the wire stays sane:
        // opened → (dismissed | user_replied). Wrapped to ensure a
        // throwing consumer never breaks the launch path.
        try {
          onFiredRef.current?.(conf.config_id ?? null);
        } catch (err) {
          console.error(
            "[useAutoLaunch] onFired_callback_failed",
            err instanceof Error ? err.message : err,
          );
        }
        incrementImpressionCount(config.tenantId, config.siteId);
        writeCooldownLastTs(config.tenantId, config.siteId);
        if (freq === "once_per_session") {
          setSessionDone(config.tenantId, config.siteId);
        }
        scheduleGreets(conf);
      }, delayMs);
    }

    async function init(): Promise<void> {
      try {
        const res = await fetch(autolaunchUrl(config), {
          method: "GET",
          headers: { accept: "application/json" },
          credentials: "omit",
        });
        const raw: unknown = await res.json().catch(() => null);
        if (cancelled) {
          return;
        }
        const conf = parseAutoLaunchPublicConfig(raw);
        if (conf.config_id) {
          const prev = readStoredConfigId(config.tenantId, config.siteId);
          if (prev && prev !== conf.config_id) {
            writeRandomUsedKeys(config.tenantId, config.siteId, []);
          }
          writeStoredConfigId(config.tenantId, config.siteId, conf.config_id);
        }
        scheduleAutoLaunch(conf);
      } catch (e) {
        console.error("[useAutoLaunch] init_failed", e instanceof Error ? e.message : e);
      }
    }

    void init();

    return () => {
      cancelled = true;
      clearLaunchTimer();
      clearGreetTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init only on tenant/site/API host primitives, not full `config` identity
  }, [config.apiOrigin, config.siteId, config.tenantId]);
}
